import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableFooter,
  TablePagination,
  Tabs,
  Tab,
  IconButton,
  Box,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import Grid from "@mui/material/Grid2";

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

const HistoryTable = ({
  fetchHistoryData,
  historyData,
  setHistoryData,
  totalCount,
  setTotalCount,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [filter, setFilter] = useState("all"); // フィルタの状態を追加

  const handleChangePage = async (event, newPage) => {
    setPage(newPage);
    const data = await fetchHistoryData(newPage + 1, rowsPerPage); // ページ番号を取得
    setHistoryData(data.records);
    setTotalCount(data.totalCount);
  };

  const handleChangeRowsPerPage = async (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0); // 最初のページにリセット
    const data = await fetchHistoryData(1, newRowsPerPage, filter);
    setHistoryData(data.records);
    setTotalCount(data.totalCount);
  };

  const handleFilterChange = async (event, newValue) => {
    setFilter(newValue);
    setPage(0); // フィルタが変更されたらページをリセット
    const data = await fetchHistoryData(1, rowsPerPage, newValue); // フィルタ条件を渡す
    setHistoryData(data.records);
    setTotalCount(data.totalCount);
  };

  const getCategoryJap = (category) => {
    switch (category) {
      case 1:
        return "ホスト側アクション";
      case 2:
        return "ゲスト側アクション";
      default:
        return "不明";
    }
  };

  return (
    <>
      <Grid container spacing={3}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={filter}
            variant="scrollable"
            onChange={handleFilterChange}
            aria-label="history filter tabs"
            textColor="secondary"
            indicatorColor="secondary"
            TabIndicatorProps={{
              style: {
                backgroundColor: theme.palette.primary.main,
                height: "3px",
              },
            }}
          >
            <Tab
              value="all"
              label="全て"
              sx={{
                color: theme.palette.text.secondary,
                minWidth: isSmallScreen ? "80px" : "90px",
                "&.Mui-selected": {
                  color: theme.palette.text.primary,
                  fontWeight: 700,
                },
              }}
            />

            <Tab
              value="host"
              label="ホスト側アクション"
              sx={{
                color: theme.palette.text.secondary,
                minWidth: isSmallScreen ? "80px" : "90px",
                "&.Mui-selected": {
                  color: theme.palette.text.primary,
                  fontWeight: 700,
                },
              }}
            />

            <Tab
              value="guest"
              label="ゲスト側アクション"
              sx={{
                color: theme.palette.text.secondary,
                minWidth: isSmallScreen ? "80px" : "90px",
                "&.Mui-selected": {
                  color: theme.palette.text.primary,
                  fontWeight: 700,
                },
              }}
            />
          </Tabs>
        </Box>

        <TableContainer component={Paper}>
          <Table aria-label="history table">
            <TableHead>
              <TableRow>
                <TableCell>作成日</TableCell>
                <TableCell>アクション</TableCell>
                <TableCell>カテゴリ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historyData.map((record) => (
                <TableRow key={record.id}>
                  <TableCell>{new Date(record[3]).toLocaleString()}</TableCell>
                  <TableCell>{record[1]}</TableCell>
                  <TableCell>{getCategoryJap(record[2])}</TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  colSpan={3}
                  count={totalCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Grid>
    </>
  );
};

export default HistoryTable;
